import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Agent Learnings (al-tutor migrations 103 + 179).
 *
 * Two scopes, one table — same shape as guardrails:
 *   - client-wide learnings   → `/learnings`         (bot_id === null)
 *   - agent learnings         → `/tutor/{id}/learnings`
 *
 * An agent listing returns the client-wide learnings that apply to the
 * agent's template alongside its own; `bot_id === null` identifies them and
 * they are read-only on the agent page.
 *
 * `embedded_at === null` means the row has no embedding yet and the agent
 * cannot find it — the page says so and offers a re-embed.
 */

export type LearningType = 'qa_example' | 'rule';
export type AnchorKind = 'question' | 'paraphrase' | 'manual';
export type AnalysisCategory =
  | 'knowledge_gap'
  | 'wrong_material'
  | 'delivery'
  | 'misunderstood'
  | 'out_of_scope'
  | 'guardrail_side_effect'
  | 'not_agent_fault';

/** Learner phrasings the learning is found by (migration 181). */
export interface LearningAnchor {
  id?: string;
  kind: AnchorKind;
  text: string;
  /** false = no vector yet, matches nothing until re-embedded */
  embedded?: boolean;
}

export interface Learning {
  id: string;
  client_id: string;
  /** null = client-wide */
  bot_id: string | null;
  type: LearningType;
  topic?: string | null;
  question?: string | null;
  content: string;
  tags: string[];
  is_active: boolean;
  /** rule applies on every turn, not only for matching questions */
  always_apply?: boolean;
  source: string;
  /** client-wide only; null = every template */
  applies_to_templates?: string[] | null;
  embedded_at?: string | null;
  embedding_model?: string | null;
  anchors?: LearningAnchor[];
  /** null = every course */
  course_id?: string | null;
  /** ISO date; after it the agent no longer finds the learning */
  valid_until?: string | null;
  /** ISO date; only flags it for review */
  review_at?: string | null;
  source_event_id?: string | null;
  canonical_id?: string | null;
  expert_note?: string | null;
  analysis_category?: AnalysisCategory | null;
  hit_count?: number;
  last_hit_at?: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LearningInput {
  type: LearningType;
  topic?: string | null;
  question?: string | null;
  content: string;
  tags: string[];
  is_active: boolean;
  always_apply?: boolean;
  applies_to_templates?: string[] | null;
  anchors?: LearningAnchor[];
  course_id?: string | null;
  valid_until?: string | null;
  review_at?: string | null;
  /** create only: the analytics turn it was made from */
  source_event_id?: string | null;
  expert_note?: string | null;
  analysis_category?: AnalysisCategory | null;
}

export interface LearningPage {
  data: Learning[];
  total: number;
}

export interface LearningSearchHit {
  learning_id: string | null;
  score: number | null;
  scope: 'bot' | 'client' | null;
  /** the anchor phrasing that matched; null = the learning's own text */
  matched_anchor?: string | null;
  /** `always`: a rule that applies on every turn (listed first, score null) */
  value: { type?: string; topic?: string; question?: string; content?: string; always?: boolean };
}

export interface LearningSearchResult {
  data: LearningSearchHit[];
  limit: number;
}

export type DraftRecommendation = 'learning' | 'knowledge_base' | 'faq' | 'no_action';

export interface LearningDraftProposal {
  recommendation: DraftRecommendation;
  analysis_category: AnalysisCategory;
  type: LearningType;
  topic: string;
  question?: string | null;
  content: string;
  anchors: string[];
  scope: 'bot' | 'client';
  scope_reason: string;
  review_in_days?: number | null;
  duplicate_of?: string | null;
  rationale: string;
}

export interface LearningDraftTurn {
  event_id: string;
  session_id?: string | null;
  bot_id?: string | null;
  bot_name?: string | null;
  template?: string | null;
  course_id?: string | null;
  course_name?: string | null;
  question: string;
  answer: string;
  rating?: 'up' | 'down' | null;
  feedback_text?: string | null;
  grounded?: boolean | null;
}

export interface SimilarLearning {
  learning_id: string;
  scope: 'bot' | 'client';
  score?: number | null;
  type: LearningType;
  topic?: string | null;
  question?: string | null;
  content: string;
  matched_anchor?: string | null;
}

export interface LearningDraft {
  turn: LearningDraftTurn;
  proposal: LearningDraftProposal;
  review_at?: string | null;
  similar: SimilarLearning[];
}

export interface ReembedResult {
  embedded: number;
  failed: number;
  skipped: number;
}

// Endpoint maximum; paging happens client-side like every other portal table.
const FETCH_LIMIT = 500;

@Injectable({ providedIn: 'root' })
export class LearningService {
  private readonly base = environment.tutorsBackend;

  constructor(private http: HttpClient) {}

  // ── Client-wide ──

  listClient(): Observable<LearningPage> {
    return this.http.get<LearningPage>(`${this.base}/learnings`, {
      params: new HttpParams().set('limit', FETCH_LIMIT),
    });
  }

  createClient(input: LearningInput): Observable<Learning> {
    return this.http.post<Learning>(`${this.base}/learnings`, input);
  }

  updateClient(id: string, input: Partial<LearningInput>): Observable<Learning> {
    const { source_event_id, expert_note, analysis_category, ...rest } = input;
    return this.http.put<Learning>(`${this.base}/learnings/${id}`, rest);
  }

  deleteClient(id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/learnings/${id}`);
  }

  reembedClient(): Observable<ReembedResult> {
    return this.http.post<ReembedResult>(`${this.base}/learnings/reembed`, {});
  }

  // ── Agent ──

  listBot(tutorId: string): Observable<LearningPage> {
    return this.http.get<LearningPage>(`${this.base}/tutor/${tutorId}/learnings`, {
      params: new HttpParams().set('limit', FETCH_LIMIT).set('include_inherited', true),
    });
  }

  createBot(tutorId: string, input: LearningInput): Observable<Learning> {
    return this.http.post<Learning>(`${this.base}/tutor/${tutorId}/learnings`, {
      ...input,
      applies_to_templates: null,
    });
  }

  updateBot(tutorId: string, id: string, input: Partial<LearningInput>): Observable<Learning> {
    // Provenance is fixed at creation; an agent row has no template scope.
    const { applies_to_templates, source_event_id, expert_note, analysis_category, ...rest } = input;
    return this.http.put<Learning>(`${this.base}/tutor/${tutorId}/learnings/${id}`, rest);
  }

  deleteBot(tutorId: string, id: string): Observable<unknown> {
    return this.http.delete(`${this.base}/tutor/${tutorId}/learnings/${id}`);
  }

  reembedBot(tutorId: string): Observable<ReembedResult> {
    return this.http.post<ReembedResult>(`${this.base}/tutor/${tutorId}/learnings/reembed`, {});
  }

  /** What the agent would get for `query` — same SQL, same model, both
   *  scopes, the agent's own top_k. `courseId` simulates a session in that
   *  course (course-bound learnings are left out without one). */
  searchTest(tutorId: string, query: string, courseId?: string | null): Observable<LearningSearchResult> {
    return this.http.post<LearningSearchResult>(
      `${this.base}/tutor/${tutorId}/learnings/search-test`,
      { query, course_id: courseId || null }
    );
  }

  // ── Curation ──

  /** Proposal from one answered turn + the expert's note. Saves nothing. */
  draftFromTurn(eventId: string, expertNote: string): Observable<LearningDraft> {
    return this.http.post<LearningDraft>(`${this.base}/learnings/draft`, {
      event_id: eventId,
      expert_note: expertNote,
    });
  }
}
